// Validación pura del horario del centro (R5.1 / D9).
//
// El Planner calcula slots a partir de `center.openingTime`/`center.closingTime`.
// Si el backend devuelve un valor ausente, no textual o con formato inválido
// (p. ej. `{ unexpected: true }`), el `.split(':')` lanza durante el render y
// la aplicación queda con `#root` vacío.
//
// Regla (R5.1): un horario NO validado nunca se sustituye por un horario
// inventado ('08:00'/'21:00'). El payload se clasifica como inválido y el
// Planner lo muestra como error recuperable.
//
// Tolerancia de formato: se acepta el mismo conjunto de cadenas que el parser
// anterior (`value.split(':').map(Number)`) manejaba sin producir `NaN`, porque
// el backend persiste el horario tal cual (`str`) y el `<input type="time">`
// no es la única fuente:
//   - hora de 1 o 2 dígitos ('8:00') y con espacio alrededor (' 08:00 ');
//   - hora 24 ('24:00' = medianoche, cierre legítimo);
//   - segmentos extra tras el segundo ('08:00:00'), ignorados como antes.
// Se rechaza lo que el parser anterior no podía manejar (la causa raíz P0):
// valores no textuales (`{ unexpected: true }`, `480`, `null`), falta de `:`,
// o partes no numéricas ('ab:cd'). Hora fuera de 0-24 y minuto fuera de 0-59
// son datos corruptos y se rechazan en vez de mascarrarse en silencio.
// No se valida el orden apertura/cierre: un centro con turno de noche
// (apertura posterior al cierre) es legítimo y produce cero slots.

export const PLANNER_SCHEDULE_REASONS = Object.freeze({
  MISSING_CENTER: 'missing-center',
  MISSING_OPENING_TIME: 'missing-opening-time',
  MISSING_CLOSING_TIME: 'missing-closing-time',
  INVALID_OPENING_TIME: 'invalid-opening-time',
  INVALID_CLOSING_TIME: 'invalid-closing-time',
});

const MAX_HOUR = 24; // 24:00 = medianoche (cierre legítimo).
const MAX_MINUTE = 59;

/**
 * Parsea una hora `H:MM` con la tolerancia del parser anterior. Devuelve null
 * si el valor no es textual o no es parseable (nunca devuelve un horario por
 * defecto).
 */
export function parseTimeOfDay(value) {
  if (typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length < 2) return null;
  // `Number(part)` reproduce la coerción del parser anterior: tolera espacios
  // y horas de un solo dígito, y `Number.isFinite` descarta 'ab', 'cd' y NaN.
  // Ojo: `Number('')` y `Number(' ')` son 0, así que una parte vacía se
  // coacciona a 0 (igual que el parser anterior) en lugar de rechazarse.
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > MAX_HOUR) return null;
  if (minute < 0 || minute > MAX_MINUTE) return null;
  return { hour, minute };
}

/**
 * Valida el horario del centro completo.
 * @returns {{valid: boolean, reason: string|null, openingTime: {hour:number,minute:number}|null, closingTime: {hour:number,minute:number}|null}}
 */
export function validateCenterSchedule(center) {
  const invalid = (reason) => ({
    valid: false,
    reason,
    openingTime: null,
    closingTime: null,
  });

  if (!center || typeof center !== 'object' || Array.isArray(center)) {
    return invalid(PLANNER_SCHEDULE_REASONS.MISSING_CENTER);
  }

  const { openingTime, closingTime } = center;

  if (openingTime === undefined || openingTime === null || openingTime === '') {
    return invalid(PLANNER_SCHEDULE_REASONS.MISSING_OPENING_TIME);
  }
  const parsedOpening = parseTimeOfDay(openingTime);
  if (!parsedOpening) {
    return invalid(PLANNER_SCHEDULE_REASONS.INVALID_OPENING_TIME);
  }

  if (closingTime === undefined || closingTime === null || closingTime === '') {
    return invalid(PLANNER_SCHEDULE_REASONS.MISSING_CLOSING_TIME);
  }
  const parsedClosing = parseTimeOfDay(closingTime);
  if (!parsedClosing) {
    return invalid(PLANNER_SCHEDULE_REASONS.INVALID_CLOSING_TIME);
  }

  return {
    valid: true,
    reason: null,
    openingTime: parsedOpening,
    closingTime: parsedClosing,
  };
}
